import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, Link } from "react-router-dom";
import appLogo from "../assets/logo/agronomist_a.svg";
import bnpLogo from "../assets/tools/yourWeather/bnp_food.svg";
import "./home.scss";
import SurveyQuestionRenderer from "./SurveyQuestionRendererExample";
import ExitSurveyModal from "./ExitSurveyModal";
import { usePoll } from "./api/pollsV2";
import { useUser, ACCESS_LEVELS } from "../services/hooks/useUser";

const SurveyHome = () => {
  const { t } = useTranslation(["survey"]);
  const history = useHistory();
  const { slug } = useParams();
  const { poll, isLoading, isError } = usePoll(slug);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const {
    user,
    isLoading: isUserLoading,
    isLoggedIn,
    isBnppClient,
  } = useUser();

  const handleExitSurvey = () => {
    // Show confirmation modal before exiting
    setIsExitModalOpen(true);
  };

  const handleConfirmExit = () => {
    // Close modal and navigate back to home
    setIsExitModalOpen(false);
    history.push("/");
  };

  const handleCancelExit = () => {
    // Just close the modal and stay on survey
    setIsExitModalOpen(false);
  };

  // Handle loading state
  if (isLoading || isUserLoading) {
    return (
      <div className="survey-home">
        <header className="survey-home__header">
          <div className="survey-home__header-content">
            <div className="survey-home__logo">
              <Link to="/">
                <img src={appLogo} alt="Agronomist" />
              </Link>
            </div>
          </div>
        </header>
        <main className="survey-home__main">
          <div style={{ textAlign: "center", padding: "2rem" }}>
            {t("survey:wizard.loading")}
          </div>
        </main>
      </div>
    );
  }

  // Handle error state
  if (isError || !poll) {
    return (
      <div className="survey-home">
        <header className="survey-home__header">
          <div className="survey-home__header-content">
            <div className="survey-home__logo">
              <Link to="/">
                <img src={appLogo} alt="Agronomist" />
              </Link>
            </div>
          </div>
        </header>
        <main className="survey-home__main">
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>{t("survey:wizard.notFound")}</p>
            <button
              onClick={() => history.push("/uzytkownik/login")}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                cursor: "pointer",
              }}
              type="button"
            >
              {t("survey:wizard.login")}
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (poll.publish_status !== 3) {
    return (
      <div className="survey-home">
        <header className="survey-home__header">
          <div className="survey-home__header-content">
            <div className="survey-home__logo">
              <Link to="/">
                <img src={appLogo} alt="Agronomist" />
              </Link>
            </div>
          </div>
        </header>
        <main className="survey-home__main">
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>{t("survey:wizard.notFound")}</p>
            <button
              onClick={() => history.push("/uzytkownik/login")}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                cursor: "pointer",
              }}
              type="button"
            >
              {t("survey:wizard.login")}
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (poll.access === ACCESS_LEVELS.LOGGED && !isLoggedIn) {
    return (
      <div className="survey-home">
        <header className="survey-home__header">
          <div className="survey-home__header-content">
            <div className="survey-home__logo">
              <Link to="/">
                <img src={appLogo} alt="Agronomist" />
              </Link>
            </div>
          </div>
        </header>
        <main className="survey-home__main">
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>{t("survey:wizard.loginRestricted")}</p>
            <button
              onClick={() => history.push("/uzytkownik/login")}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                cursor: "pointer",
              }}
              type="button"
            >
              {t("survey:wizard.login")}
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (poll.access === ACCESS_LEVELS.BNPP_CLIENT && !isBnppClient) {
    return (
      <div className="survey-home">
        <header className="survey-home__header">
          <div className="survey-home__header-content">
            <div className="survey-home__logo">
              <Link to="/">
                <img src={appLogo} alt="Agronomist" />
              </Link>
            </div>
          </div>
        </header>
        <main className="survey-home__main">
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>{t("survey:wizard.clientRestricted")}</p>
            <button
              onClick={() => history.push("/")}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                cursor: "pointer",
              }}
              type="button"
            >
              {t("survey:wizard.goHome")}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="survey-home">
      {/* Header/Navigation */}
      <header className="survey-home__header">
        <div className="survey-home__header-content">
          <div className="survey-home__logo">
            <Link to="/">
              <img src={appLogo} alt="Agronomist" />
            </Link>
          </div>
          <button
            className="survey-home__exit-button"
            onClick={handleExitSurvey}
            type="button"
          >
            <svg
              className="survey-home__exit-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9"
                stroke="#585A5C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 17L21 12L16 7"
                stroke="#585A5C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21 12H9"
                stroke="#585A5C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="survey-home__exit-text">
              {t("survey:wizard.exitSurvey")}
            </span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="survey-home__main">
        <SurveyQuestionRenderer
          pollId={poll ? poll.id : null}
          poll={poll ? poll.content.questions : null}
        />
      </main>

      {/* Footer */}
      <footer className="survey-home__footer">
        <div className="survey-home__footer-content">
          <div className="survey-home__footer-logo">
            <img src={bnpLogo} alt="BNP Paribas Food & Agro" />
          </div>
          <p className="survey-home__footer-text">
            Created by{" "}
            <span className="survey-home__footer-link">300.CODES</span>
          </p>
        </div>
      </footer>

      {/* Exit Confirmation Modal */}
      <ExitSurveyModal
        isOpen={isExitModalOpen}
        onClose={handleConfirmExit}
        onConfirm={handleCancelExit}
      />
    </div>
  );
};

export default SurveyHome;
